/**
 * API service for all backend communication.
 *
 * Uses the auth store for token management.
 * No direct Supabase access.
 */

import axios from 'axios'
import { useAuthStore } from '../stores/auth-store'

const API_URL = import.meta.env.VITE_API_URL || '/api'

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  const token = await useAuthStore.getState().getAccessToken()

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Don't send X-Organization-Id header - let backend use active_organization_id
  // from the user's profile to avoid stale/race-condition issues with persisted state

  return config
})

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Session expired, sign out
      await useAuthStore.getState().signOut()
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Dashboard API
export const dashboardApi = {
  getSummary: async () => (await api.get('/v1/dashboard/summary')).data,
  getStats: async () => (await api.get('/v1/dashboard/stats')).data,
  getRecentActivity: async () => (await api.get('/v1/dashboard/activity')).data
}

// Companies API
export interface Contact {
  id?: string
  name: string | null
  title: string | null
  email: string | null
  phone: string | null
  is_primary: boolean
}

export interface CompanyUpdateData {
  name?: string
  website?: string
  address?: string
  city?: string
  country?: string
  phone?: string
  email?: string
  company_email?: string
  company_emails?: string[]
  phone_numbers?: string[]
  contact_person?: string
  contact_title?: string
  contacts?: Contact[]
  vat_number?: string
  established_year?: number
  employee_count?: string
  business_type?: string
  delivery_area?: string
  payment_methods?: string
  delivery_types?: string
}

export interface CallStartResponse {
  success: boolean
  message: string
  job_id: string
  status_url: string
  call_session_id: string
}

export interface CompanyCallSession {
  id: string
  organization_id: string
  raw_company_id: string
  initiated_by?: string | null
  job_id?: string | null
  phone_number?: string | null
  status:
    | 'queued'
    | 'dialing'
    | 'in_progress'
    | 'completed'
    | 'failed'
    | 'no_answer'
    | 'busy'
    | 'cancelled'
  booking_status: 'not_attempted' | 'slots_offered' | 'booked' | 'failed' | 'declined'
  booked_provider?: 'google' | 'microsoft' | null
  booked_event_id?: string | null
  booked_start_at?: string | null
  booked_end_at?: string | null
  booked_timezone?: string | null
  summary?: string | null
  error?: string | null
  created_at?: string | null
  updated_at?: string | null
  completed_at?: string | null
}

export const companiesApi = {
  list: async (params?: { page?: number; per_page?: number; stage?: string; search?: string; source?: string }) =>
    (await api.get('/v1/companies', { params })).data,
  getSources: async (): Promise<{ source_name: string; count: number }[]> =>
    (await api.get('/v1/companies/sources')).data,
  get: async (id: string) => {
    const payload = (await api.get(`/v1/companies/${id}`)).data
    return payload?.data ?? payload
  },
  update: async (id: string, data: CompanyUpdateData) => {
    const payload = (await api.patch(`/v1/companies/${id}`, data)).data
    return payload?.data ?? payload
  },
  delete: async (id: string) => (await api.delete(`/v1/companies/${id}`)).data,
  getStageCounts: async () => (await api.get('/v1/companies/stage-counts')).data,
  qualify: async (ids: string[]) => (await api.post('/v1/qualification/qualify', { company_ids: ids })).data,
  getActivities: async (id: string) =>
    (await api.get(`/v1/companies/${id}/activities`)).data,
  getOutreachPreview: async (id: string) =>
    (await api.get(`/v1/companies/${id}/outreach-preview`)).data,
  getOutreachEmail: async (id: string) =>
    (await api.get(`/v1/companies/${id}/outreach-email`)).data,
  runPipeline: async (
    ids: string[],
    options?: { sendEmail?: boolean; step?: 'qualify' | 'contact' | 'analyze' | 'outreach' }
  ) =>
    (
      await api.post('/v1/companies/run-pipeline', {
        company_ids: ids,
        send_email: options?.sendEmail,
        step: options?.step,
      })
    ).data,
  sendOutreach: async (id: string, body?: { email_body?: string }) =>
    (
      await api.post(`/v1/companies/${id}/send-outreach`, body ?? {})
    ).data,
  call: async (id: string): Promise<CallStartResponse> =>
    (await api.post(`/v1/companies/${id}/call`)).data,
  listCalls: async (id: string, params?: { limit?: number }): Promise<{ calls: CompanyCallSession[] }> =>
    (await api.get(`/v1/companies/${id}/calls`, { params })).data,
  exclude: async (id: string): Promise<{ is_excluded: boolean }> =>
    (await api.post(`/v1/companies/${id}/exclude`)).data,
  include: async (id: string): Promise<{ is_excluded: boolean }> =>
    (await api.post(`/v1/companies/${id}/include`)).data,
  importCompanies: async (
    name: string, 
    file: File, 
    columnMapping?: Record<string, string>
  ): Promise<{ import_id: string; status: string; message: string }> => {
    const formData = new FormData()
    formData.append('name', name)
    formData.append('file', file)
    if (columnMapping && Object.keys(columnMapping).length > 0) {
      formData.append('column_mapping', JSON.stringify(columnMapping))
    }
    return (await api.post('/v1/companies/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data
  },
  getImportStatus: async (importId: string): Promise<{
    id: string
    status: string
    total_rows?: number
    created_count?: number
    updated_count?: number
    skipped_count?: number
    error_count?: number
    errors?: Array<{ row: number; field?: string; message: string }>
  }> => (await api.get(`/v1/companies/imports/${importId}`)).data,
  listImports: async (params?: { page?: number; per_page?: number }): Promise<{
    imports: Array<{
      id: string
      name: string
      status: string
      total_rows: number
      created_count: number
      updated_count: number
      error_count: number
      created_at: string
    }>
    total: number
  }> => (await api.get('/v1/companies/imports', { params })).data,
}

export type CalendarProvider = 'google' | 'microsoft'

export interface CalendarConnection {
  id: string
  provider: CalendarProvider
  account_email?: string | null
  status: 'active' | 'expired' | 'revoked' | 'error'
  is_default: boolean
  token_expires_at?: string | null
  scopes?: string[]
  provider_metadata?: Record<string, unknown>
  created_at?: string | null
  updated_at?: string | null
}

export const integrationsApi = {
  listCalendarConnections: async (): Promise<{
    connections: CalendarConnection[]
    default_provider: CalendarProvider | null
    has_default: boolean
  }> => (await api.get('/v1/integrations/calendar/connections')).data,
  startCalendarOAuth: async (
    provider: CalendarProvider,
    returnTo?: string
  ): Promise<{ provider: CalendarProvider; authorization_url: string; state: string }> =>
    (await api.post(`/v1/integrations/calendar/${provider}/start`, { return_to: returnTo })).data,
  setDefaultCalendarProvider: async (
    provider: CalendarProvider
  ): Promise<{ default_provider: CalendarProvider; connection: CalendarConnection }> =>
    (await api.patch('/v1/integrations/calendar/default-provider', { provider })).data,
  disconnectCalendarConnection: async (connectionId: string): Promise<{ success: boolean }> =>
    (await api.delete(`/v1/integrations/calendar/connections/${connectionId}`)).data,
}

// Email Integration types
export type EmailProvider = 'google' | 'microsoft' | 'smtp'
export type EmailHealthStatus = 'healthy' | 'unhealthy' | 'unknown'

export interface EmailConnection {
  id: string
  provider: EmailProvider
  email_address: string
  display_name?: string | null
  is_active: boolean
  is_default: boolean
  health_status: EmailHealthStatus
  health_checked_at?: string | null
  daily_send_count: number
  last_used_at?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface SMTPConnectionData {
  email_address: string
  display_name?: string
  smtp_host: string
  smtp_port: number
  smtp_username: string
  smtp_password: string
  smtp_use_tls?: boolean
}

export const emailIntegrationsApi = {
  listConnections: async (): Promise<{
    connections: EmailConnection[]
    default_connection_id: string | null
    has_connection: boolean
  }> => (await api.get('/v1/integrations/email/connections')).data,

  startOAuth: async (
    provider: 'google' | 'microsoft',
    returnTo?: string
  ): Promise<{ provider: EmailProvider; authorization_url: string; state: string }> =>
    (await api.post(`/v1/integrations/email/${provider}/start`, { return_to: returnTo })).data,

  saveSMTPConnection: async (data: SMTPConnectionData): Promise<{
    connection: EmailConnection
    success: boolean
  }> => (await api.post('/v1/integrations/email/smtp', data)).data,

  setDefaultConnection: async (connectionId: string): Promise<{
    default_connection: { id: string; provider: EmailProvider; email_address: string }
    success: boolean
  }> => (await api.patch(`/v1/integrations/email/connections/${connectionId}/default`)).data,

  testConnection: async (connectionId: string): Promise<{
    connection_id: string
    provider: EmailProvider
    email_address: string
    is_valid: boolean
    health_status: EmailHealthStatus
  }> => (await api.post(`/v1/integrations/email/connections/${connectionId}/test`)).data,

  sendTestEmail: async (
    connectionId: string,
    toEmail: string,
    subject?: string,
    body?: string
  ): Promise<{
    success: boolean
    message_id?: string
    provider: EmailProvider
  }> =>
    (
      await api.post(`/v1/integrations/email/connections/${connectionId}/send-test`, {
        to_email: toEmail,
        subject: subject || 'Test email from Prospect Hunter',
        body: body || 'This is a test email to verify your email connection is working.',
      })
    ).data,

  disconnectConnection: async (connectionId: string): Promise<{ success: boolean }> =>
    (await api.delete(`/v1/integrations/email/connections/${connectionId}`)).data,
}

// Sources API
export interface SearchConfig {
  terms: string[]
  max_pages: number
  filters?: Record<string, unknown>
}

export interface SourceCreateData {
  url: string
  name?: string
  description?: string
  source_type?: string
  region?: string
  is_active?: boolean
  industry_tags?: string[]
  search_config?: SearchConfig
}

export interface SourceUpdateData {
  quality_rating?: number
  scraper_status?: string
  is_active?: boolean
  review_notes?: string
  search_config?: SearchConfig
  industry_tags?: string[]
}

export interface DiscoveryResult {
  added_count: number
  reasoning: string
  skipped_count: number
  reused_count: number
  catalog_count: number
}

// Source relationship type classification
export type SourceRelationshipType =
  | 'competitor_platform'
  | 'complementary_platform'
  | 'industry_directory'
  | 'official_registry'
  | 'contact_enrichment'
  | 'general_directory'

// Source Profile metadata (shared across organizations)
export interface SourceProfile {
  id: string
  url: string
  name: string
  description?: string
  source_type: string
  covered_countries: string[]
  covered_regions: string[]
  languages: string[]
  primary_industries: string[]
  data_fields_available: string[]
  estimated_listings?: string
  times_discovered: number
  times_scraped: number
  avg_quality_rating?: number
  country_distribution: Record<string, number>
  total_companies_found: number
  is_registry?: boolean
  registry_country?: string
  access_type?: string
  contact_data_quality?: string
  data_freshness?: string
  is_curated?: boolean
  geographic_scope?: string
  use_case_description?: string
  platform_domain_tags?: string[]
}

export const sourcesApi = {
  list: async (params?: { recheck?: boolean }) =>
    (await api.get('/v1/sources', { params })).data,
  get: async (id: string) => (await api.get(`/v1/sources/${id}`)).data,
  create: async (data: SourceCreateData) => (await api.post('/v1/sources', data)).data,
  update: async (id: number, data: SourceUpdateData) => (await api.patch(`/v1/sources/${id}`, data)).data,
  runScrape: async (id: string) => (await api.post(`/v1/sources/${id}/scrape`)).data,
  getResolvedTerms: async (id: number) => (await api.get(`/v1/sources/${id}/resolved-terms`)).data,
  generateTerms: async (sourceId: number) => (await api.post(`/v1/sources/${sourceId}/generate-search-terms`)).data,
  discover: async () => (await api.post<DiscoveryResult>('/v1/sources/discover')).data,
  accept: async (sourceId: number) => (await api.post(`/v1/sources/${sourceId}/accept`)).data,
  dismiss: async (sourceId: number) => (await api.post(`/v1/sources/${sourceId}/dismiss`)).data,
  delete: async (sourceId: number) => (await api.delete(`/v1/sources/${sourceId}`)).data,
  stop: async (sourceId: number) => (await api.post(`/v1/sources/${sourceId}/stop`)).data
}

// Jobs API
export const jobsApi = {
  list: async (params?: { page?: number; limit?: number }) => (await api.get('/v1/jobs', { params })).data,
  get: async (id: string) => (await api.get(`/v1/jobs/${id}`)).data,
  cancel: async (id: string) => (await api.post(`/v1/jobs/${id}/cancel`)).data
}

// Lists API
export type ListRunStatus =
  | 'queued'
  | 'enhancing_query'
  | 'discovering_sources'
  | 'running_sources'
  | 'completed'
  | 'failed'
  | 'cancelled'

export type ListRunSourceOrigin = 'existing' | 'discovered'
export type ListRunSourceExecutionStatus = 'executed' | 'queued_suggested' | 'skipped'
export type ListRunCompanyMatchStatus = 'added_to_pipeline' | 'already_in_pipeline'

export interface ListRunSourceSummary {
  id: number
  run_id: string
  source_id?: number | null
  source_name?: string | null
  source_url?: string | null
  source_relationship?: string | null
  source_origin: ListRunSourceOrigin
  execution_status: ListRunSourceExecutionStatus
  execution_order?: number | null
  result_summary: Record<string, unknown>
  created_at?: string | null
}

export interface ListRunSummary {
  id: string
  organization_id: string
  created_by?: string | null
  raw_query: string
  enhanced_query?: string | null
  status: ListRunStatus
  error?: string | null
  metrics: Record<string, unknown>
  execution_terms: string[]
  created_at: string
  started_at?: string | null
  completed_at?: string | null
  updated_at?: string | null
}

export interface ListRunDetail extends ListRunSummary {
  sources: ListRunSourceSummary[]
}

export interface ListRunCompanyRow {
  id: number
  run_id: string
  raw_company_id: string
  source_id?: number | null
  source_name?: string | null
  match_status: ListRunCompanyMatchStatus
  created_at?: string | null
  company_name?: string | null
  website?: string | null
  company_source?: string | null
  country?: string | null
  city_state?: string | null
  industry?: string | null
  business_type?: string | null
  company_type?: string | null
  company_type_tags?: string[]
}

export interface ListRunCompaniesPage {
  items: ListRunCompanyRow[]
  page: number
  per_page: number
  total: number
}

type ListRunStreamEventName =
  | 'connected'
  | 'run_status'
  | 'company_matched'
  | 'run_completed'
  | 'heartbeat'
  | 'error'

export interface ListRunStreamEvent {
  event: ListRunStreamEventName | string
  data: unknown
}

interface StreamListRunEventsOptions {
  afterId?: number
  signal?: AbortSignal
  onEvent: (event: ListRunStreamEvent) => void
}

async function parseSseStream(
  stream: ReadableStream<Uint8Array>,
  onEvent: (event: ListRunStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const emitChunk = (chunk: string) => {
    const lines = chunk.split('\n')
    let event = 'message'
    const dataLines: string[] = []

    for (const rawLine of lines) {
      const line = rawLine.trimEnd()
      if (!line || line.startsWith(':')) continue
      if (line.startsWith('event:')) {
        event = line.slice(6).trim()
        continue
      }
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart())
      }
    }

    if (!dataLines.length) return
    const payloadRaw = dataLines.join('\n')
    try {
      onEvent({ event, data: JSON.parse(payloadRaw) })
    } catch {
      onEvent({ event, data: payloadRaw })
    }
  }

  while (true) {
    if (signal?.aborted) {
      await reader.cancel()
      return
    }
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')

    let boundary = buffer.indexOf('\n\n')
    while (boundary >= 0) {
      const chunk = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      emitChunk(chunk)
      boundary = buffer.indexOf('\n\n')
    }
  }
}

export const listsApi = {
  createRun: async (
    query: string,
    options?: { expand_search?: boolean }
  ): Promise<ListRunSummary> =>
    (await api.post('/v1/lists/runs', { query, ...(options || {}) })).data,
  listRuns: async (params?: { limit?: number }): Promise<ListRunSummary[]> =>
    (await api.get('/v1/lists/runs', { params })).data,
  getRun: async (runId: string): Promise<ListRunDetail> =>
    (await api.get(`/v1/lists/runs/${runId}`)).data,
  listRunCompanies: async (
    runId: string,
    params?: {
      page?: number
      per_page?: number
      search?: string
      stage?: string
      source?: string
    }
  ): Promise<ListRunCompaniesPage> =>
    (await api.get(`/v1/lists/runs/${runId}/companies`, { params })).data,
  getRunCompanyStageCounts: async (
    runId: string,
    params?: { search?: string; source?: string }
  ): Promise<Record<string, number>> =>
    (await api.get(`/v1/lists/runs/${runId}/companies/stage-counts`, { params })).data,
  getRunCompanySources: async (
    runId: string
  ): Promise<{ source_name: string; count: number }[]> =>
    (await api.get(`/v1/lists/runs/${runId}/companies/sources`)).data,
  cancelRun: async (runId: string): Promise<ListRunSummary> =>
    (await api.post(`/v1/lists/runs/${runId}/cancel`)).data,
  executeRunSource: async (
    runId: string,
    sourceRowId: number
  ): Promise<{ status: string; source_row_id: string }> =>
    (await api.post(`/v1/lists/runs/${runId}/sources/${sourceRowId}/execute`)).data,
  streamRunEvents: async (
    runId: string,
    options: StreamListRunEventsOptions
  ): Promise<void> => {
    const token = await useAuthStore.getState().getAccessToken()
    const base = API_URL.replace(/\/$/, '')
    const afterId = options.afterId ?? 0
    const url = `${base}/v1/lists/runs/${encodeURIComponent(runId)}/events?after_id=${afterId}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal: options.signal,
      credentials: 'same-origin',
    })

    if (!response.ok || !response.body) {
      throw new Error(`Stream request failed (${response.status})`)
    }

    await parseSseStream(response.body, options.onEvent, options.signal)
  },
}

export type ProspectRecommendationStatus = 'new' | 'accepted' | 'dismissed' | 'expired'

export interface RecommendedProspectCompany {
  id: string
  name?: string | null
  website?: string | null
  source?: string | null
  company_type?: string | null
  company_type_tags: string[]
  industry_tags: string[]
  offering_tags: string[]
  profile_summary?: string | null
  profile_freshness: Record<string, unknown>
  contact_availability: Record<string, unknown>
}

export interface RecommendedProspectRow {
  id: string
  organization_id: string
  raw_company_id: string
  fit_score: number
  fit_reasons: string[]
  feature_breakdown: Record<string, unknown>
  status: ProspectRecommendationStatus
  reviewed_by?: string | null
  reviewed_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  company: RecommendedProspectCompany
}

export interface RecommendedProspectsPage {
  items: RecommendedProspectRow[]
  page: number
  per_page: number
  total: number
}

export const prospectsApi = {
  listRecommended: async (params?: {
    page?: number
    per_page?: number
    status?: ProspectRecommendationStatus
  }): Promise<RecommendedProspectsPage> =>
    (await api.get('/v1/prospects/recommended', { params })).data,
  acceptRecommended: async (recommendationId: string): Promise<RecommendedProspectRow> =>
    (await api.post(`/v1/prospects/recommended/${recommendationId}/accept`)).data?.data,
  dismissRecommended: async (recommendationId: string): Promise<RecommendedProspectRow> =>
    (await api.post(`/v1/prospects/recommended/${recommendationId}/dismiss`)).data?.data,
}

// Scrapers API
export const scrapersApi = {
  list: async () => (await api.get('/v1/scrapers')).data,
  run: async (
    source: string,
    options?: { max_pages?: number; max_companies?: number; resume?: boolean }
  ) => (await api.post('/v1/scrapers/run', { source, ...options })).data,
  runAll: async () => (await api.post('/v1/scrapers/run-all')).data
}

// Qualification API
export const qualificationApi = {
  run: async (source?: string) => (await api.post('/v1/qualification/run', { source })).data,
  qualify: async (companyIds: string[]) =>
    (await api.post('/v1/qualification/qualify', { company_ids: companyIds })).data,
  enrichWebPresence: async (source?: string, limit?: number, runId?: string) =>
    (await api.post('/v1/qualification/enrich-web-presence', { source, limit, run_id: runId })).data
}

// Outreach API
export const outreachApi = {
  generate: async (source?: string, limit?: number, runId?: string) =>
    (await api.post('/v1/outreach/generate', { source, limit, run_id: runId })).data,
  send: async (source?: string, limit?: number, runId?: string) =>
    (await api.post('/v1/outreach/send', { source, limit, run_id: runId })).data
}

// Outreach Templates API
export interface OutreachTemplate {
  id: string
  organization_id: string
  name: string
  role_label: string
  subject: string
  html_body: string
  text_body: string
  is_default: boolean
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export interface OutreachTemplateCreate {
  name: string
  role_label: string
  subject: string
  html_body: string
  text_body?: string
  is_default?: boolean
  is_active?: boolean
}

export interface OutreachTemplateUpdate {
  name?: string
  role_label?: string
  subject?: string
  html_body?: string
  text_body?: string
  is_default?: boolean
  is_active?: boolean
}

export const outreachTemplatesApi = {
  list: async (): Promise<OutreachTemplate[]> =>
    (await api.get('/v1/outreach/templates')).data,
  generate: async (): Promise<OutreachTemplate[]> =>
    (await api.post('/v1/outreach/templates/generate')).data,
  get: async (id: string): Promise<OutreachTemplate> =>
    (await api.get(`/v1/outreach/templates/${id}`)).data,
  create: async (data: OutreachTemplateCreate): Promise<OutreachTemplate> =>
    (await api.post('/v1/outreach/templates', data)).data,
  update: async (id: string, data: OutreachTemplateUpdate): Promise<OutreachTemplate> =>
    (await api.put(`/v1/outreach/templates/${id}`, data)).data,
  delete: async (id: string): Promise<void> =>
    (await api.delete(`/v1/outreach/templates/${id}`)).data
}

// Campaigns API
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed'
export type OutreachStatus = 'pending' | 'ready' | 'sent' | 'replied' | 'bounced'

export interface CampaignStats {
  total_companies: number
  pending: number
  ready: number
  sent: number
  replied: number
  bounced: number
}

export interface Campaign {
  id: string
  organization_id: string
  name: string
  description?: string | null
  status: CampaignStatus
  created_by?: string | null
  created_at?: string | null
  updated_at?: string | null
  stats: CampaignStats
}

export interface CampaignsListResponse {
  items: Campaign[]
  page: number
  per_page: number
  total: number
}

export interface CampaignCompany {
  id: string
  campaign_id: string
  raw_company_id: string
  outreach_status: OutreachStatus
  added_at?: string | null
  company_name?: string | null
  website?: string | null
  country?: string | null
  business_type?: string | null
  contact_name?: string | null
  contact_email?: string | null
  has_outreach: boolean
  pipeline_stage?: string | null
}

export interface CampaignCompaniesResponse {
  items: CampaignCompany[]
  page: number
  per_page: number
  total: number
}

export interface CampaignCreate {
  name: string
  description?: string
  status?: CampaignStatus
}

export interface CampaignUpdate {
  name?: string
  description?: string
  status?: CampaignStatus
}

export interface CampaignLaunchResponse {
  job_id: string
  companies_queued: number
}

export const campaignsApi = {
  list: async (params?: {
    page?: number
    per_page?: number
    status?: CampaignStatus
  }): Promise<CampaignsListResponse> =>
    (await api.get('/v1/campaigns', { params })).data,
  get: async (id: string): Promise<Campaign> =>
    (await api.get(`/v1/campaigns/${id}`)).data,
  create: async (data: CampaignCreate): Promise<Campaign> =>
    (await api.post('/v1/campaigns', data)).data,
  update: async (id: string, data: CampaignUpdate): Promise<Campaign> =>
    (await api.patch(`/v1/campaigns/${id}`, data)).data,
  delete: async (id: string): Promise<void> =>
    (await api.delete(`/v1/campaigns/${id}`)).data,
  listCompanies: async (
    campaignId: string,
    params?: {
      page?: number
      per_page?: number
      outreach_status?: OutreachStatus
      search?: string
    }
  ): Promise<CampaignCompaniesResponse> =>
    (await api.get(`/v1/campaigns/${campaignId}/companies`, { params })).data,
  addCompanies: async (
    campaignId: string,
    rawCompanyIds: string[]
  ): Promise<{ message: string }> =>
    (await api.post(`/v1/campaigns/${campaignId}/companies`, { raw_company_ids: rawCompanyIds })).data,
  removeCompanies: async (
    campaignId: string,
    rawCompanyIds: string[]
  ): Promise<{ message: string }> =>
    (await api.delete(`/v1/campaigns/${campaignId}/companies`, { data: { raw_company_ids: rawCompanyIds } })).data,
  launch: async (
    campaignId: string,
    options?: { send_email?: boolean; limit?: number }
  ): Promise<CampaignLaunchResponse> =>
    (await api.post(`/v1/campaigns/${campaignId}/launch`, options ?? {})).data,
}

// Deals/CRM API
export type DealStage =
  | 'contacted'
  | 'interested'
  | 'meeting_scheduled'
  | 'proposal'
  | 'won'
  | 'lost'

export type ActivityType =
  | 'note'
  | 'email_sent'
  | 'email_received'
  | 'call'
  | 'meeting'
  | 'stage_change'

export interface DealCompanyInfo {
  id: string
  name?: string | null
  website?: string | null
  country?: string | null
  business_type?: string | null
}

export interface DealActivity {
  id: string
  deal_id: string
  activity_type: ActivityType
  content?: string | null
  metadata: Record<string, unknown>
  performed_by?: string | null
  performer_name?: string | null
  created_at?: string | null
}

export interface Deal {
  id: string
  organization_id: string
  raw_company_id: string
  campaign_id?: string | null
  campaign_name?: string | null
  stage: DealStage
  title: string
  contact_name?: string | null
  contact_email?: string | null
  expected_value?: number | null
  created_by?: string | null
  created_at?: string | null
  updated_at?: string | null
  closed_at?: string | null
  company?: DealCompanyInfo | null
}

export interface DealDetail extends Deal {
  activities: DealActivity[]
}

export interface DealsListResponse {
  items: Deal[]
  page: number
  per_page: number
  total: number
}

export interface DealStageCounts {
  contacted: number
  interested: number
  meeting_scheduled: number
  proposal: number
  won: number
  lost: number
}

export interface DealSummary {
  stage_counts: DealStageCounts
  total_expected_value: number
  value_by_stage: Record<string, number>
}

export interface DealCreate {
  raw_company_id: string
  campaign_id?: string
  stage?: DealStage
  title: string
  contact_name?: string
  contact_email?: string
  expected_value?: number
}

export interface DealUpdate {
  stage?: DealStage
  title?: string
  contact_name?: string
  contact_email?: string
  expected_value?: number
}

export interface AddActivityRequest {
  activity_type: ActivityType
  content?: string
  metadata?: Record<string, unknown>
}

export const dealsApi = {
  list: async (params?: {
    page?: number
    per_page?: number
    stage?: DealStage
    campaign_id?: string
    search?: string
  }): Promise<DealsListResponse> =>
    (await api.get('/v1/deals', { params })).data,
  get: async (id: string): Promise<DealDetail> =>
    (await api.get(`/v1/deals/${id}`)).data,
  create: async (data: DealCreate): Promise<Deal> =>
    (await api.post('/v1/deals', data)).data,
  update: async (id: string, data: DealUpdate): Promise<Deal> =>
    (await api.patch(`/v1/deals/${id}`, data)).data,
  delete: async (id: string): Promise<void> =>
    (await api.delete(`/v1/deals/${id}`)).data,
  addActivity: async (
    dealId: string,
    data: AddActivityRequest
  ): Promise<DealActivity> =>
    (await api.post(`/v1/deals/${dealId}/activities`, data)).data,
  getStageCounts: async (campaignId?: string): Promise<DealStageCounts> =>
    (await api.get('/v1/deals/stage-counts', { params: { campaign_id: campaignId } })).data,
  getSummary: async (campaignId?: string): Promise<DealSummary> =>
    (await api.get('/v1/deals/summary', { params: { campaign_id: campaignId } })).data,
}
